import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Check, X, Loader2, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { ModeToggle } from "../components/ModeToggle";
import { AvatarPicker } from "../components/AvatarPicker";
import { QLogo } from "../components/branding/QLogo";
import { LegalModal, TERMS_TITLE, PRIVACY_TITLE, TermsContent, PrivacyContent } from "../components/legal/LegalModal";
import { PRESET_AVATARS } from "../data/avatars";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const ONDATAG_RE = /^[a-z0-9_]{3,20}$/;
const REF_KEY = "queonda.referral.ref";
const EMERGENT_AUTH_URL = "https://auth.emergentagent.com/";

function readStoredRef() {
  try {
    const v = localStorage.getItem(REF_KEY);
    if (!v) return null;
    const norm = v.replace(/^@/, "").trim().toLowerCase();
    return ONDATAG_RE.test(norm) ? norm : null;
  } catch (_e) {
    return null;
  }
}

/**
 * Emergent Google Auth returns to the profile page with `#session_id=<...>`.
 * We parse it here and hand it to our backend so a proper JWT is issued.
 */
function extractSessionIdFromHash() {
  if (typeof window === "undefined") return null;
  const raw = window.location.hash || "";
  if (!raw) return null;
  const clean = raw.startsWith("#") ? raw.slice(1) : raw;
  try {
    const params = new URLSearchParams(clean);
    const sid = params.get("session_id");
    return sid ? sid.trim() : null;
  } catch (_e) {
    return null;
  }
}

export default function AuthPage() {
  const { login, signup, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [tab, setTab] = useState(() => (readStoredRef() ? "signup" : "login"));
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [referralTag, setReferralTag] = useState(() => readStoredRef());
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  // Login form
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Signup form
  const [suEmail, setSuEmail] = useState("");
  const [suPassword, setSuPassword] = useState("");
  const [suName, setSuName] = useState("");
  const [suTag, setSuTag] = useState("");
  const [suAvatar, setSuAvatar] = useState(PRESET_AVATARS[0]);

  const [tagState, setTagState] = useState({ status: "idle", available: null });

  const normalizedTag = suTag.toLowerCase().replace(/^@/, "").trim();
  const tagValid = ONDATAG_RE.test(normalizedTag);

  // On mount / whenever the URL search params change: pick up ?ref= and
  // persist it in localStorage so it survives navigation / reload. If a
  // fresh ref lands via URL, switch to the signup tab automatically.
  useEffect(() => {
    const raw = searchParams.get("ref");
    if (!raw) return;
    const norm = raw.replace(/^@/, "").trim().toLowerCase();
    if (!ONDATAG_RE.test(norm)) return;
    try {
      localStorage.setItem(REF_KEY, norm);
    } catch (_e) {
      /* ignore */
    }
    setReferralTag(norm);
    setTab("signup");
  }, [searchParams]);

  useEffect(() => {
    if (!tagValid) {
      setTagState({ status: normalizedTag ? "invalid" : "idle", available: null });
      return;
    }
    let cancelled = false;
    setTagState({ status: "checking", available: null });
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/auth/check-ondatag?tag=${encodeURIComponent(normalizedTag)}`);
        if (!cancelled) {
          setTagState({ status: "checked", available: data.available });
        }
      } catch {
        if (!cancelled) setTagState({ status: "idle", available: null });
      }
    }, 380);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [normalizedTag, tagValid]);

  // Handle Google Auth callback: Emergent redirects back with #session_id=...
  // We consume that once on mount and swap it for our own JWT.
  useEffect(() => {
    const sid = extractSessionIdFromHash();
    if (!sid) return;
    setGoogleBusy(true);
    // Strip the fragment from the URL so a reload doesn't re-fire the exchange.
    try {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    } catch (_e) {
      /* ignore */
    }
    (async () => {
      try {
        const ref = readStoredRef();
        const u = await loginWithGoogle({
          session_id: sid,
          referred_by: ref || undefined,
          terms_accepted: true,
        });
        try {
          localStorage.removeItem(REF_KEY);
        } catch (_e) {
          /* ignore */
        }
        toast.success(`¡Qué onda, ${(u.display_name || "").split(" ")[0] || "amig@"}!`);
        navigate("/");
      } catch (_err) {
        toast.error("Error al conectar con Google. Intenta con tu correo.");
      } finally {
        setGoogleBusy(false);
      }
    })();
  }, []);

  const startGoogleAuth = () => {
    if (googleBusy) return;
    const currentUrl = typeof window !== "undefined" ? window.location.origin + "/auth" : "";
    if (!currentUrl) {
      toast.error("Error al conectar con Google. Intenta con tu correo.");
      return;
    }
    setGoogleBusy(true);
    try {
      // Emergent Managed Google Auth: user is redirected to Google, then back
      // to our /auth with `#session_id=...` in the URL fragment.
      const target = `${EMERGENT_AUTH_URL}?redirect=${encodeURIComponent(currentUrl)}`;
      window.location.href = target;
    } catch (_e) {
      setGoogleBusy(false);
      toast.error("Error al conectar con Google. Intenta con tu correo.");
    }
  };

  const onLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login({ email: loginEmail.trim(), password: loginPassword });
      toast.success(`¡Qué onda, ${u.display_name.split(" ")[0]}!`);
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const onSignup = async (e) => {
    e.preventDefault();
    if (!tagValid) {
      toast.error("Tu @OndaTag debe tener 3-20 caracteres (minúsculas, números, _).");
      return;
    }
    if (tagState.available === false) {
      toast.error(`El @OndaTag @${normalizedTag} ya está en uso.`);
      return;
    }
    if (suPassword.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    setBusy(true);
    try {
      const u = await signup({
        email: suEmail.trim(),
        password: suPassword,
        display_name: suName.trim(),
        ondatag: normalizedTag,
        avatar: suAvatar,
        referred_by: referralTag || undefined,
        terms_accepted: true,
      });
      // Clear referral marker on success — one-shot per install.
      try {
        localStorage.removeItem(REF_KEY);
      } catch (_e) {
        /* ignore */
      }
      toast.success(`¡Bienvenido a la red, ${u.display_name.split(" ")[0]}!`);
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full flex flex-col" data-testid="auth-page">
      <div className="flex items-center justify-between px-5 py-4">
        <div className="flex items-center gap-2">
          <QLogo size={38} />
          <span className="font-display text-lg font-bold tracking-wide">
            <span className="notranslate" translate="no">
              Qué Onda <span className="q-accent-text">Chat</span>
            </span>
          </span>
        </div>
        <ModeToggle compact />
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-10">
        <div className="w-full max-w-md q-glass rounded-3xl p-6 sm:p-8 q-page-enter" data-testid="auth-card">
          <div className="mb-5">
            <p className="q-chip">
              <span
                className="h-1.5 w-1.5 rounded-full q-accent-bg"
                style={{ boxShadow: "0 0 8px var(--accent-glow)" }}
              />
              Fase 1a · Acceso seguro
            </p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold mt-3 leading-tight">
              ¡Qué onda! <span className="q-accent-text">Conéctate</span> a la red.
            </h1>
            <p className="text-sm text-slate-400 mt-2">
              Sin números de teléfono. Solo tú, tu correo y tu @OndaTag.
            </p>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            {/* Google 1-click primary action — sits above the email/tabs form
                so it's unmistakably the fast path. */}
            <button
              type="button"
              onClick={startGoogleAuth}
              disabled={googleBusy}
              data-testid="google-signin"
              className="w-full inline-flex items-center justify-center gap-3 mb-4 rounded-full py-3 px-5 font-semibold text-slate-900 transition-transform active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                background:
                  "linear-gradient(180deg, #ffffff 0%, #f3f5f7 100%)",
                border: "1px solid rgba(255,255,255,0.5)",
                boxShadow:
                  "0 8px 22px rgba(0,0,0,0.25), 0 0 22px rgba(0,240,255,0.18)",
              }}
              aria-label="Continuar con Google"
            >
              {googleBusy ? (
                <Loader2 size={18} className="animate-spin text-slate-700" />
              ) : (
                <GoogleGlyph />
              )}
              <span>Continuar con Google</span>
            </button>

            <div
              className="flex items-center gap-2 mb-4"
              aria-hidden="true"
            >
              <span className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.08)" }} />
              <span className="text-[10px] uppercase tracking-[0.24em] font-mono text-slate-500">
                o con tu correo
              </span>
              <span className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.08)" }} />
            </div>

            <TabsList
              className="grid grid-cols-2 w-full bg-black/40 border border-white/10 rounded-full p-1"
              data-testid="auth-tabs"
            >
              <TabsTrigger
                value="login"
                data-testid="tab-login"
                className="rounded-full font-display uppercase tracking-wider text-xs data-[state=active]:bg-[color:var(--accent)] data-[state=active]:text-[color:var(--accent-contrast)] transition-colors"
              >
                Iniciar sesión
              </TabsTrigger>
              <TabsTrigger
                value="signup"
                data-testid="tab-signup"
                className="rounded-full font-display uppercase tracking-wider text-xs data-[state=active]:bg-[color:var(--accent)] data-[state=active]:text-[color:var(--accent-contrast)] transition-colors"
              >
                Crear cuenta
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-5">
              <form onSubmit={onLogin} className="space-y-4" data-testid="login-form">
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-400 font-display">
                    Correo electrónico
                  </label>
                  <input
                    data-testid="login-email"
                    type="email"
                    autoComplete="email"
                    required
                    className="q-input mt-1"
                    placeholder="tu@correo.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-400 font-display">
                    Contraseña
                  </label>
                  <div className="relative mt-1">
                    <input
                      data-testid="login-password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      required
                      minLength={8}
                      className="q-input pr-11"
                      placeholder="••••••••"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                      aria-label={showPassword ? "Ocultar" : "Mostrar"}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <button
                  data-testid="login-submit"
                  disabled={busy}
                  className="q-btn-primary w-full flex items-center justify-center gap-2 mt-2"
                >
                  {busy && <Loader2 size={16} className="animate-spin" />}
                  Entrar a la red
                </button>
                <p className="text-xs text-slate-500 text-center">
                  ¿Primera vez por aquí?{" "}
                  <button
                    type="button"
                    className="q-accent-text hover:underline"
                    onClick={() => setTab("signup")}
                  >
                    Crea tu cuenta
                  </button>
                </p>
              </form>
            </TabsContent>

            <TabsContent value="signup" className="mt-5">
              {referralTag && (
                <div
                  className="mb-4 flex items-center gap-2 px-3 py-2 rounded-2xl q-glass border"
                  style={{
                    borderColor: "rgba(0, 240, 255, 0.35)",
                    background: "rgba(0, 240, 255, 0.06)",
                    boxShadow: "0 0 18px rgba(0, 240, 255, 0.18)",
                  }}
                  data-testid="ref-chip"
                >
                  <span
                    className="h-6 w-6 rounded-full flex items-center justify-center shrink-0"
                    style={{
                      background: "rgba(0, 240, 255, 0.18)",
                      border: "1px solid rgba(0, 240, 255, 0.45)",
                    }}
                    aria-hidden="true"
                  >
                    <Sparkles size={13} className="q-accent-text" />
                  </span>
                  <span className="text-sm text-slate-100">
                    Te invitó <span className="q-accent-text font-mono">@{referralTag}</span>
                  </span>
                </div>
              )}
              <form onSubmit={onSignup} className="space-y-4" data-testid="signup-form">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-400 font-display">
                      Nombre visible
                    </label>
                    <input
                      data-testid="signup-name"
                      required
                      minLength={1}
                      maxLength={40}
                      className="q-input mt-1"
                      placeholder="Tu nombre"
                      value={suName}
                      onChange={(e) => setSuName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-400 font-display">
                      @OndaTag
                    </label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-slate-500">
                        @
                      </span>
                      <input
                        data-testid="signup-ondatag"
                        required
                        className="q-input pl-8 pr-9 font-mono lowercase"
                        placeholder="cibernauta99"
                        value={suTag}
                        onChange={(e) => setSuTag(e.target.value)}
                      />
                      <span
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                        data-testid="ondatag-status"
                      >
                        {tagState.status === "checking" && (
                          <Loader2 size={16} className="animate-spin text-slate-400" />
                        )}
                        {tagState.status === "checked" && tagState.available && (
                          <Check size={18} className="q-accent-text" />
                        )}
                        {tagState.status === "checked" && !tagState.available && (
                          <X size={18} className="text-rose-400" />
                        )}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1 font-mono">
                      3-20 · a-z 0-9 _
                    </p>
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-400 font-display">
                    Correo electrónico
                  </label>
                  <input
                    data-testid="signup-email"
                    type="email"
                    autoComplete="email"
                    required
                    className="q-input mt-1"
                    placeholder="tu@correo.com"
                    value={suEmail}
                    onChange={(e) => setSuEmail(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-400 font-display">
                    Contraseña
                  </label>
                  <div className="relative mt-1">
                    <input
                      data-testid="signup-password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      required
                      minLength={8}
                      className="q-input pr-11"
                      placeholder="Mínimo 8 caracteres"
                      value={suPassword}
                      onChange={(e) => setSuPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-400 font-display block mb-2">
                    Elige tu avatar
                  </label>
                  <AvatarPicker
                    selected={suAvatar}
                    onSelect={setSuAvatar}
                    name={suName || "?"}
                    testIdPrefix="signup-avatar-option"
                  />
                </div>

                <button
                  data-testid="signup-submit"
                  disabled={busy}
                  className="q-btn-primary w-full flex items-center justify-center gap-2 mt-1"
                >
                  {busy && <Loader2 size={16} className="animate-spin" />}
                  Unirse ahora
                </button>
                <p className="text-xs text-slate-500 text-center">
                  ¿Ya tienes cuenta?{" "}
                  <button
                    type="button"
                    className="q-accent-text hover:underline"
                    onClick={() => setTab("login")}
                  >
                    Inicia sesión
                  </button>
                </p>
              </form>
            </TabsContent>
          </Tabs>

          {/* Legal consent — shared for both email and Google flows */}
          <p
            className="mt-5 text-[11px] leading-relaxed text-slate-500 text-center"
            data-testid="legal-consent"
          >
            Al continuar, aceptas nuestros{" "}
            <button
              type="button"
              className="q-accent-text hover:underline font-medium"
              onClick={() => setShowTerms(true)}
              data-testid="open-terms"
            >
              Términos de Servicio
            </button>{" "}
            y{" "}
            <button
              type="button"
              className="q-accent-text hover:underline font-medium"
              onClick={() => setShowPrivacy(true)}
              data-testid="open-privacy"
            >
              Política de Privacidad
            </button>
            .
          </p>
        </div>
      </div>

      <div className="text-center pb-6 text-[11px] text-slate-500 font-mono tracking-widest uppercase">
        · sin teléfono · sin llamadas · sin ubicación ·
      </div>

      <LegalModal
        open={showTerms}
        title={TERMS_TITLE}
        onClose={() => setShowTerms(false)}
        testId="terms-modal"
      >
        <TermsContent />
      </LegalModal>
      <LegalModal
        open={showPrivacy}
        title={PRIVACY_TITLE}
        onClose={() => setShowPrivacy(false)}
        testId="privacy-modal"
      >
        <PrivacyContent />
      </LegalModal>
    </div>
  );
}

// Inline SVG for the Google G glyph — self-contained, no icon deps.
function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
      <path fill="none" d="M0 0h48v48H0z" />
    </svg>
  );
}
